import { USER_LOAN_PROGRAM } from '@/config/constants';

/**
 * Starting down payment for a program when the buyer hasn't chosen one for
 * this property (a flat 20% anchored FHA buyers on the wrong number).
 */
export function defaultDownPaymentPercent(loanProgram?: string | null): number {
  switch (loanProgram) {
    case USER_LOAN_PROGRAM.FHA:
      return 0.035;
    case USER_LOAN_PROGRAM.VA:
    case USER_LOAN_PROGRAM.USDA:
      return 0;
    case USER_LOAN_PROGRAM.CONVENTIONAL:
    case USER_LOAN_PROGRAM.DEFAULT:
      return 0.05;
    case USER_LOAN_PROGRAM.DSCR:
    case USER_LOAN_PROGRAM.COMMERCIAL:
    case USER_LOAN_PROGRAM.FIX_AND_FLIP:
    case USER_LOAN_PROGRAM.BRIDGE:
      return 0.25;
    case USER_LOAN_PROGRAM.CASH:
      return 1;
    default:
      return 0.2;
  }
}

const finitePositive = (v: unknown): number | null => {
  const n = Number(v);
  return v != null && v !== '' && Number.isFinite(n) && n >= 0 ? n : null;
};

/**
 * The one down payment every screen shows for a property:
 *   1. the amount saved for this property (`product.savedDownPayment`)
 *   2. the buyer's profile down payment, if it fits under the price
 *   3. the program's default percentage of the price
 *
 * Stage 1, the estimate, property cards, the offer form and the pre-qual
 * letter each used their own chain before (one fell back to $0, another to
 * 5%, another to the profile), so the same property showed five different
 * down payments.
 */
export function resolvePropertyDownPayment(opts: {
  savedDownPayment?: number | null;
  profileDownPayment?: number | null;
  price?: number | null;
  loanProgram?: string | null;
}): number {
  const price = finitePositive(opts.price) ?? 0;
  const saved = finitePositive(opts.savedDownPayment);
  if (saved != null) return price > 0 ? Math.min(saved, price) : saved;
  const profile = finitePositive(opts.profileDownPayment);
  if (profile != null && profile > 0 && (price <= 0 || profile < price)) {
    return profile;
  }
  return Math.round(price * defaultDownPaymentPercent(opts.loanProgram));
}

/** resolvePropertyDownPayment for a Product (offer price, saved/profile program). */
export function downPaymentForProduct(
  product:
    | {
        price?: number | null;
        savedDownPayment?: number | null;
        savedLoanType?: string | null;
        loanInfo?: { offerPrice?: number | null } | null;
        user?: {
          loanProgram?: string | null;
          loanInfo?: { downPayment?: number | null } | null;
        } | null;
      }
    | null
    | undefined,
  opts: { loanProgram?: string | null; profileDownPayment?: number | null } = {}
): number {
  if (!product) return 0;
  return resolvePropertyDownPayment({
    savedDownPayment: product.savedDownPayment,
    profileDownPayment:
      opts.profileDownPayment ?? product.user?.loanInfo?.downPayment,
    price: product.loanInfo?.offerPrice ?? product.price,
    loanProgram:
      opts.loanProgram ??
      product.savedLoanType?.toLowerCase() ??
      product.user?.loanProgram,
  });
}

/** Lowest down payment the sliders allow for a program (share of price). */
export function minDownPaymentPercent(loanProgram?: string | null): number {
  switch (loanProgram) {
    case USER_LOAN_PROGRAM.VA:
    case USER_LOAN_PROGRAM.USDA:
      return 0;
    case USER_LOAN_PROGRAM.FHA:
      return 0.035;
    case USER_LOAN_PROGRAM.CONVENTIONAL:
    case USER_LOAN_PROGRAM.DEFAULT:
      return 0.03;
    case USER_LOAN_PROGRAM.DSCR:
    case USER_LOAN_PROGRAM.COMMERCIAL:
    case USER_LOAN_PROGRAM.FIX_AND_FLIP:
    case USER_LOAN_PROGRAM.BRIDGE:
      return 0.2;
    default:
      return 0.05;
  }
}
